import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";

const eventIdSchema = z.object({ eventId: z.string().uuid() });
const addSweeperSchema = z.object({ eventId: z.string().uuid(), userId: z.string().uuid() });
const removeSweeperSchema = z.object({ roleId: z.string().uuid() });

async function assertOrganizer(supabase: any, userId: string, eventId: string) {
  const { data: evt, error } = await supabase
    .from("events")
    .select("id,organizer_id")
    .eq("id", eventId)
    .maybeSingle();
  if (error || !evt) throw new Error("Evento no encontrado");
  if (evt.organizer_id !== userId) throw new Error("No autorizado");
}

export const getEventSweepers = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => eventIdSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    await assertOrganizer(supabase, userId, data.eventId);

    const { data: rows, error } = await supabase
      .from("event_safety_roles")
      .select("id,user_id,role,profiles(full_name)")
      .eq("event_id", data.eventId)
      .eq("role", "sweeper");
    if (error) throw error;

    return {
      sweepers: (rows ?? []).map((r: any) => ({
        id: r.id,
        user_id: r.user_id,
        full_name: r.profiles?.full_name ?? null,
      })),
    };
  });

export const getEligibleSweeperCandidates = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => eventIdSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    await assertOrganizer(supabase, userId, data.eventId);

    const { data: inscritos, error: insErr } = await supabase
      .from("inscripciones")
      .select("usuario_id")
      .eq("evento_id", data.eventId);
    if (insErr) throw insErr;

    const { data: sweepers, error: swpErr } = await supabase
      .from("event_safety_roles")
      .select("user_id")
      .eq("event_id", data.eventId);
    if (swpErr) throw swpErr;

    const sweeperIds = new Set((sweepers ?? []).map((s: any) => s.user_id));
    const candidateIds = (inscritos ?? [])
      .map((i: any) => i.usuario_id)
      .filter((id: string) => id !== userId && !sweeperIds.has(id));

    if (!candidateIds.length) return { candidates: [] as Array<{ id: string; full_name: string | null }> };

    const { data: profiles, error: profErr } = await supabase
      .from("profiles")
      .select("id,full_name")
      .in("id", candidateIds);
    if (profErr) throw profErr;

    const profileMap = new Map((profiles ?? []).map((p: any) => [p.id, p.full_name]));
    return {
      candidates: candidateIds.map((id: string) => ({
        id,
        full_name: profileMap.get(id) ?? null,
      })),
    };
  });

export const addEventSweeper = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => addSweeperSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    await assertOrganizer(supabase, userId, data.eventId);

    const { error } = await supabase.from("event_safety_roles").insert({
      event_id: data.eventId,
      user_id: data.userId,
      role: "sweeper",
    });
    if (error) throw error;
    return { ok: true };
  });

export const removeEventSweeper = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => removeSweeperSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;

    const { data: role, error: fetchErr } = await supabase
      .from("event_safety_roles")
      .select("id,event_id")
      .eq("id", data.roleId)
      .maybeSingle();
    if (fetchErr) throw fetchErr;
    if (!role) throw new Error("Rol no encontrado");

    await assertOrganizer(supabase, userId, role.event_id);

    const { error } = await supabase.from("event_safety_roles").delete().eq("id", data.roleId);
    if (error) throw error;
    return { ok: true };
  });
